import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Printer, FileDown, Search, Image as ImageIcon } from "lucide-react";
import { api, assetUrl, errMsg } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const RowBtn = ({ children, onClick, t, title, danger, disabled }) => (
  <button onClick={onClick} title={title} disabled={disabled} data-testid={t}
    className={`p-1.5 rounded hover:bg-slate-700 text-slate-400 disabled:opacity-40 ${danger ? "hover:text-red-400" : "hover:text-white"}`}>{children}</button>
);

export default function CardsTable({ cards, fields, templates, editingId, busy, reload, onEdit, onPrint, onCustomer }) {
  const { user: me } = useAuth();
  const trial = !!me?.is_trial && !me?.impersonated;
  const lockTip = "Subscribe to a plan to edit or delete cards";
  const [q, setQ] = useState("");
  const [by, setBy] = useState("all");
  const searchable = fields.filter((f) => ["CERT_NO", "CUSTOMER_NAME", "MOBILE"].includes(f.key));
  const [sel, setSel] = useState(new Set());
  const [toDelete, setToDelete] = useState(null);
  const textCols = fields.filter((f) => f.type !== "image").slice(0, 5);
  const photoKey = fields.find((f) => f.type === "image")?.key;
  const layoutName = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return "—";
    return templates.filter((x) => x.name === t.name).length > 1 ? `${t.name} #${t.id.slice(0, 4)}` : t.name;
  };

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return cards;
    const hit = (v) => String(v ?? "").toLowerCase().includes(s);
    return cards.filter((c) => (by === "all" ? Object.values(c.data).some(hit) : hit(c.data[by])));
  }, [cards, q, by]);
  const selectedCards = cards.filter((c) => sel.has(c.id));
  const allShown = shown.length > 0 && shown.every((c) => sel.has(c.id));

  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSel(allShown ? new Set() : new Set(shown.map((c) => c.id)));
  const remove = async () => {
    try {
      await api.delete(`/cards/${toDelete.id}`);
      setSel((s) => { const n = new Set(s); n.delete(toDelete.id); return n; });
      toast.success("Card deleted");
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setToDelete(null); }
  };

  return (
    <section className="rounded-xl border border-slate-800 bg-slate-900/60" data-testid="cards-section">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-slate-800">
        <p className="font-heading font-bold">Saved cards <span className="text-slate-500 font-mono text-sm" data-testid="cards-count">({cards.length})</span></p>
        <div className="flex flex-wrap items-center gap-2">
          <select className="cf-input h-8 text-xs" style={{ width: 150 }} value={by} onChange={(e) => setBy(e.target.value)} data-testid="cards-search-by">
            <option value="all">Search all fields</option>
            {searchable.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input className="cf-input h-8 text-xs w-56" style={{ paddingLeft: 30 }} placeholder={by === "all" ? "Cert no, mobile, name…" : `Search ${fields.find((f) => f.key === by)?.label || ""}…`} value={q} onChange={(e) => setQ(e.target.value)} data-testid="cards-search-input" />
          </div>
          <span className="text-xs text-slate-400" data-testid="cards-selected-count">{sel.size} selected</span>
          <button className="cf-btn-ghost h-8 text-xs" disabled={!sel.size || busy} onClick={() => onPrint("print", selectedCards)} data-testid="cards-print-selected-button"><Printer size={13} /> Print selected</button>
          <button className="cf-btn-primary h-8 text-xs px-3" disabled={!sel.size || busy} onClick={() => onPrint("pdf", selectedCards)} data-testid="cards-pdf-selected-button"><FileDown size={13} /> PDF selected</button>
        </div>
      </div>
      {cards.length === 0 ? (
        <p className="p-8 text-center text-sm text-slate-500" data-testid="cards-empty">No cards saved yet. Fill the form above and click “Add card”.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="cards-table">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-slate-500 border-b border-slate-800">
                <th className="px-4 py-3 w-10"><Checkbox checked={allShown} onCheckedChange={toggleAll} data-testid="cards-select-all" /></th>
                {photoKey && <th className="px-2 py-3 w-12" />}
                {textCols.map((f) => <th key={f.key} className="px-4 py-3 whitespace-nowrap">{f.label}</th>)}
                <th className="px-4 py-3">Last layout</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr><td colSpan={textCols.length + 4} className="px-4 py-8 text-center text-slate-500" data-testid="cards-no-match">No cards match “{q}”.</td></tr>
              )}
              {shown.map((c, i) => (
                <tr key={c.id} className={`border-b border-slate-800/60 hover:bg-slate-800/30 ${editingId === c.id ? "bg-amber-500/5" : ""}`} data-testid={`card-row-${i}`}>
                  <td className="px-4 py-2"><Checkbox checked={sel.has(c.id)} onCheckedChange={() => toggle(c.id)} data-testid={`card-checkbox-${i}`} /></td>
                  {photoKey && (
                    <td className="px-2 py-2">
                      <div className="w-8 h-8 rounded bg-slate-800 overflow-hidden grid place-items-center">
                        {c.data[photoKey] ? <img src={assetUrl(c.data[photoKey])} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={13} className="text-slate-600" />}
                      </div>
                    </td>
                  )}
                  {textCols.map((f) => (
                    <td key={f.key} className="px-4 py-2 max-w-[180px] truncate text-slate-300">
                      {f.key === "CUSTOMER_NAME" && c.data[f.key] && onCustomer ? (
                        <button onClick={() => onCustomer(c)} className="text-amber-300 hover:underline underline-offset-2" title="View customer history" data-testid={`card-customer-${i}`}>{String(c.data[f.key])}</button>
                      ) : String(c.data[f.key] ?? "")}
                    </td>
                  ))}
                  <td className="px-4 py-2 text-slate-500 truncate max-w-[160px]">{layoutName(c.layout_id)}</td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <RowBtn onClick={() => onEdit(c)} t={`card-edit-${i}`} title={trial ? lockTip : "Edit"} disabled={trial}><Pencil size={13} /></RowBtn>
                    <RowBtn onClick={() => onPrint("print", [c])} t={`card-print-${i}`} title="Print this card" disabled={busy}><Printer size={13} /></RowBtn>
                    <RowBtn onClick={() => onPrint("pdf", [c])} t={`card-pdf-${i}`} title="PDF of this card" disabled={busy}><FileDown size={13} /></RowBtn>
                    <RowBtn onClick={() => setToDelete(c)} t={`card-delete-${i}`} title={trial ? lockTip : "Delete"} danger disabled={trial}><Trash2 size={13} /></RowBtn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this card?</AlertDialogTitle>
            <AlertDialogDescription>{String(Object.values(toDelete?.data || {})[0] ?? "")} will be removed permanently.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="card-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-500" data-testid="card-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
